import { User } from '../interfaces/user-interfaces';
import { Onboarding } from '../interfaces/onboarding-interfaces';
import { ROLES, normalizeUserRole } from '../auth/roles';
import { PERMISSIONS } from '../auth/permissions';
import { deskInPlan, STAYS_HOME } from './products';

// Path prefixes that must render WITHOUT authentication (and without the
// dashboard's data-fetching hooks). The auth-provider uses this to avoid
// bouncing tokenless visitors to login, and the root router uses it to avoid
// mounting AppRouter (whose hooks fire authed fetches) on these routes.
export const PUBLIC_PREFIXES = [
  '/signup',
  '/verify-email',
  '/confirm-email-change',
  '/invite',
  '/forgot-password',
  '/reset-password',
  // The policies (privacy, terms, cookies, DPA, security). Linked from the
  // sign-in and signup screens, so they must open without a session.
  '/legal',
  // A visitor pass shared with a guest (resident portal Plan 6): the guest has
  // no account. Without this they are bounced to sign-in.
  '/p',
  // Lobbero Stays (plan S3): a stay guest's check-in link (/s/:token, and
  // /s/return where the ID partner sends them back) and one adult's own link
  // (/sg/:token). Neither guest has an account.
  '/s',
  '/sg',
];

export const isPublicPath = (path: string): boolean =>
  PUBLIC_PREFIXES.some(p => path === p || path.startsWith(`${p}/`));

// Public paths that must ALSO render for a visitor who is already signed in.
//
// The rest of PUBLIC_PREFIXES is bounced home for an authenticated user (rule
// #15) so nobody clobbers their own session by wandering onto /signup. An invite
// link and a password-reset link are the opposite case: they are a HANDOFF, and
// the person holding the link is usually NOT the person the browser is signed in
// as. The admin who created the invite is the likeliest one to click it — to
// test it, or because the invitee is sitting next to them — and bouncing them
// home meant the accept page never rendered and the password could never be set.
// That made every invitation, for every role, unusable from a browser that had
// ever signed in.
//
// The pages themselves are responsible for ending the incumbent session before
// they mint the new one (see invite-accept-page.tsx), so rendering here does not
// leave two identities half-applied.
export const HANDOFF_PREFIXES = ['/invite', '/reset-password'];

export const isHandoffPath = (path: string): boolean =>
  HANDOFF_PREFIXES.some(p => path === p || path.startsWith(`${p}/`));

// ── EVERY public path that must render for a signed-in visitor ──
//
// The handoff pair above, PLUS /verify-email — which is the same bug in a
// different disguise and stayed broken after the invitation one was fixed.
//
// The person clicking a verification link is, almost by definition, ALREADY
// SIGNED IN: they signed up minutes ago, the app left them on their dashboard,
// and the mail arrived in the next tab. The #15 bounce fired on exactly that
// person, so VerifyEmailPage never mounted, POST /verify-email never ran, and
// the one-time token sat unconsumed until it expired. The account then reported
// itself unverified forever, no matter how many times the link was clicked — and
// there was nothing to see in the server log, because the request was never made.
//
// It is NOT in HANDOFF_PREFIXES, and the distinction is load-bearing: a handoff
// page ENDS the incumbent session before minting a different identity, and doing
// that here would sign somebody out for confirming their own address. Verifying
// is an edit to the record of whoever is already here. The page re-reads GET
// /auth on success (refreshAuth) instead, so the session picks up the change.
export const SIGNED_IN_PUBLIC_PREFIXES = [
  ...HANDOFF_PREFIXES,
  '/verify-email',
  // Same reasoning as /verify-email, and then some: this link is opened in the
  // NEW mailbox, which is as likely to be read on the signed-in machine as on a
  // phone that has never seen Lobbero. Both have to work.
  '/confirm-email-change',
  // A signed-in person reads the same policies from their account page. Reading
  // a policy neither needs nor touches the session, so there is nothing to
  // bounce them away from.
  '/legal',
  // A resident opening the pass link they just shared must see the guest's
  // page, not be sent home. NOT a handoff: it ends nobody's session.
  '/p',
  // A signed-in host previewing a stay guest's check-in link must still see
  // the guest's page. Not a handoff either.
  '/s',
  '/sg',
];

export const rendersWhenSignedIn = (path: string): boolean =>
  SIGNED_IN_PUBLIC_PREFIXES.some(p => path === p || path.startsWith(`${p}/`));

// Single source of truth for "where does this user belong after auth". Onboarding
// takes precedence: an incomplete first-run always routes to /onboarding, so no
// role lands in its dashboard before finishing setup.
//
// STAFF ROLE FIRST, THEN THE PORTAL (migration 037)
//
// A person can now be both — an owner who lives in one of their own buildings
// holds `owner` AND an open occupancy, so both branches match. Their staff home
// wins because that is the surface they came to work in; the portal stays fully
// reachable from the navigation, it just is not where they land.
//
// The portal branch tests the `portal:read` PERMISSION rather than a role,
// because since 037 residency is not a role at all — the server derives that
// permission from an open occupancy on every request. `permissions` therefore
// has to be passed in; defaulting it to [] would send every resident to '/'.
export const resolvePostAuthRoute = (
  user: User | null | undefined,
  onboarding?: Onboarding | null,
  permissions: readonly string[] = []
): string => {
  // ── A LOBBERO OPERATOR WITH NO STAFF ROLE ────────────────────────────────
  //
  // FIRST, AND AHEAD OF THE ONBOARDING REDIRECT. An internal operations account
  // holds `users.role = null` and no occupancy, so without this branch it fell
  // through every case below to '/' — which renders the SIGN-IN FORM. The
  // person had a valid session and was looking at a login page.
  //
  // The `!role` test is what keeps this narrow. A customer's owner who is ALSO
  // a platform admin still lands on their own /dashboard, because that is the
  // surface they signed in to work in; they reach the console from the menu.
  // Only the account with no staff role anywhere — the one that has nothing
  // else to land on — is sent to /admin.
  //
  // Ahead of the onboarding check because an operator account never runs the
  // customer onboarding wizard, and being bounced into it would trap them in a
  // flow with no exit.
  if (user?.is_platform_admin && !normalizeUserRole(user?.role)) return '/admin';

  if (onboarding && !onboarding.completed) return '/onboarding';

  // normalizeUserRole: this is users.role, which holds a staff capability or
  // nothing. A legacy 'tenant'/'resident' value resolves to null here and is
  // routed by permission below, exactly like a modern null-role resident.
  switch (normalizeUserRole(user?.role)) {
    case ROLES.CONCIERGE:
      // Without Desk there is no front desk to open (Independent Stays IS1):
      // a concierge of a Stays-only organisation is a hotel's RECEPTION and
      // lands on its screen (IS9). /reception is gated on stays:desk, which a
      // concierge always holds, and never redirects back here, so the guards
      // cannot loop.
      return deskInPlan(user) ? '/main-menu' : '/reception';
    case ROLES.OWNER:
    case ROLES.PROPERTY_MANAGER:
      // A Stays-only organisation (Independent Stays IS1) has no Desk home: its
      // owner and managers land on Stays. Absent `products` = Desk, as before.
      // IS3: a new Stays-only owner lands on the setup guide until their first
      // live listing settles the first run (first_run_done, stays_host only).
      if (deskInPlan(user)) return '/dashboard';
      if (onboarding?.flow === 'stays_host' && onboarding.first_run_done === false) return `${STAYS_HOME}/setup`;

      return STAYS_HOME;
    // Independent Stays IS5: a co-host's home is their listings.
    case ROLES.STAYS_COHOST:
      return STAYS_HOME;
    default:
      break;
  }

  if (permissions.includes(PERMISSIONS.PORTAL_READ)) return '/portal';

  // The other half of the branch at the top: an account that is a platform
  // admin AND holds no product surface at all still has somewhere to go.
  if (user?.is_platform_admin) return '/admin';

  // ── SIGNED IN, AND NOTHING LEFT TO OPEN (resident-portal RP-9.9) ──────────
  //
  // No staff role, no open occupancy, not an operator: a resident who has
  // moved out. This used to return '/', which renders the SIGN-IN FORM — to
  // somebody who is signed in, and whom signing in again sends straight back.
  // The portal's own guard (RequirePermission) shows them the "you no longer
  // have a home" screen, so that is where they go. Nobody signed in is ever
  // sent to the sign-in form; '/' is for no session at all.
  if (user) return '/portal';

  return '/';
};
